import { Component, OnInit } from '@angular/core';
import { AgentService, AgentToolInvocation } from '../../services/agent.service';
import { fadeInOut } from '../../services/animations';

interface ChatTurn {
  role: 'user' | 'assistant' | 'system';
  text: string;
  tools?: AgentToolInvocation[];
  inputTokens?: number;
  outputTokens?: number;
  cachedInputTokens?: number;
  pending?: boolean;
  error?: string;
}

@Component({
  selector: 'app-agent-chat',
  templateUrl: './agent-chat.component.html',
  styleUrls: ['./agent-chat.component.scss'],
  animations: [fadeInOut],
  standalone: false
})
export class AgentChatComponent implements OnInit {
  conversationId: string | null = null;
  turns: ChatTurn[] = [];
  draft: string = '';
  busy = false;
  agentName = 'Admin Query Agent';
  modelName = 'gpt-5-mini';
  showCostMeter = false;

  // Week-2 placeholder: we'll wire streaming here. For Week 1 thin slice this is JSON.
  constructor(private agents: AgentService) {}

  async ngOnInit() {
    await this.ensureConversation();
  }

  private async ensureConversation(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.agents.createAdminQueryConversation().subscribe({
        next: (conv) => {
          this.conversationId = conv.conversationId;
          resolve();
        },
        error: (err) => {
          this.turns.push({
            role: 'system',
            text: 'Could not start a new agent conversation. Check that you have the "Use Admin Query Agent" permission.',
            error: err?.message ?? 'unknown error'
          });
          reject(err);
        }
      });
    });
  }

  async send(): Promise<void> {
    if (!this.draft.trim() || this.busy) return;
    if (!this.conversationId) {
      await this.ensureConversation();
      if (!this.conversationId) return;
    }

    const userText = this.draft.trim();
    this.turns.push({ role: 'user', text: userText });
    this.draft = '';
    this.busy = true;

    const placeholder: ChatTurn = { role: 'assistant', text: '', pending: true };
    this.turns.push(placeholder);

    this.agents.sendAdminQueryMessage(this.conversationId, userText).subscribe({
      next: (resp) => {
        placeholder.text = resp.assistantMessage;
        placeholder.tools = resp.toolInvocations || [];
        placeholder.inputTokens = resp.usage?.inputTokens;
        placeholder.outputTokens = resp.usage?.outputTokens;
        placeholder.cachedInputTokens = resp.usage?.cachedInputTokens;
        placeholder.pending = false;
        this.busy = false;
      },
      error: (err) => {
        placeholder.text = '';
        placeholder.error = err?.error?.detail ?? err?.message ?? 'Agent turn failed.';
        placeholder.pending = false;
        this.busy = false;
      }
    });
  }

  reset(): void {
    this.turns = [];
    this.conversationId = null;
    this.ensureConversation();
  }

  toggleCostMeter(): void { this.showCostMeter = !this.showCostMeter; }
}
